import React, { useMemo, useState } from 'react';
import { Box, Button, ButtonGroup, Stack, Typography } from '@mui/joy';

const CHART_WIDTH = 800;
const CHART_HEIGHT = 100;
const STORAGE_KEY = 'aiProcessingTimeChartMode';

function loadMode() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'stacked' || stored === 'dual-line') return stored;
    } catch (_) {
        // ignore
    }
    return 'stacked';
}

function deriveMetrics(record) {
    const batch = Math.max(0, (record.processing_end_ms ?? 0) - (record.processing_start_ms ?? 0));
    const ai = Math.max(0, record.ai_processing_ms ?? 0);
    const overhead = Math.max(0, batch - ai);
    return { batch, ai, overhead };
}

/**
 * AI processing time lane — stacked bars (default) or dual-line chart.
 */
export default function AIProcessingTimeLane({
    aiData,
    startTime,
    endTime,
    rulerDuration,
    mode: controlledMode,
    onModeChange,
}) {
    const [internalMode, setInternalMode] = useState(loadMode);
    const mode = controlledMode ?? internalMode;

    const setMode = (next) => {
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch (_) {
            // ignore
        }
        if (onModeChange) onModeChange(next);
        else setInternalMode(next);
    };

    const relevantData = useMemo(() => {
        if (!aiData?.length) return [];
        return aiData.filter((item) => {
            const start = item.processing_start_ms;
            const end = item.processing_end_ms;
            return start != null && end != null && end >= startTime && start <= endTime;
        });
    }, [aiData, startTime, endTime]);

    const maxMs = useMemo(() => {
        if (!relevantData.length) return 1000;
        let max = 0;
        for (const record of relevantData) {
            const { batch, ai } = deriveMetrics(record);
            max = Math.max(max, batch, ai);
        }
        return Math.max(1000, max * 1.1);
    }, [relevantData]);

    if (!relevantData.length) {
        return (
            <Box sx={{ py: 2 }}>
                <Typography level="body-sm" textColor="neutral.500">
                    No AI processing time data for this window
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography level="body-sm" sx={{ color: 'neutral.700', fontWeight: 'bold' }}>
                    AI Processing Time ({relevantData.length} windows)
                </Typography>
                <ButtonGroup size="sm" variant="outlined">
                    <Button
                        variant={mode === 'stacked' ? 'solid' : 'outlined'}
                        onClick={() => setMode('stacked')}
                    >
                        Stacked bar
                    </Button>
                    <Button
                        variant={mode === 'dual-line' ? 'solid' : 'outlined'}
                        onClick={() => setMode('dual-line')}
                    >
                        Dual line
                    </Button>
                </ButtonGroup>
            </Stack>

            <Box
                sx={{
                    height: '100px',
                    backgroundColor: 'neutral.50',
                    borderRadius: 'sm',
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'neutral.200',
                }}
            >
                <svg
                    width="100%"
                    height="100%"
                    viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                    preserveAspectRatio="none"
                    style={{ position: 'absolute' }}
                >
                    <defs>
                        <pattern id="ai-time-grid" width="40" height="20" patternUnits="userSpaceOnUse">
                            <path d="M 40 0 L 0 0 0 20" fill="none" stroke="#e0e0e0" strokeWidth="0.5" opacity="0.3" />
                        </pattern>
                    </defs>
                    <rect width={CHART_WIDTH} height={CHART_HEIGHT} fill="url(#ai-time-grid)" />

                    {mode === 'stacked'
                        ? relevantData.map((record, index) => {
                              const blockStart = Math.max(record.processing_start_ms, startTime);
                              const blockEnd = Math.min(record.processing_end_ms, endTime);
                              const xStart = ((blockStart - startTime) / rulerDuration) * CHART_WIDTH;
                              const xEnd = ((blockEnd - startTime) / rulerDuration) * CHART_WIDTH;
                              const width = Math.max(xEnd - xStart, 2);
                              const { ai, overhead } = deriveMetrics(record);
                              const aiHeight = (ai / maxMs) * CHART_HEIGHT * 0.9;
                              const overheadHeight = (overhead / maxMs) * CHART_HEIGHT * 0.9;
                              const aiY = CHART_HEIGHT - aiHeight;
                              const overheadY = aiY - overheadHeight;

                              return (
                                  <g key={record.id || index}>
                                      <rect
                                          x={xStart}
                                          y={aiY}
                                          width={width}
                                          height={aiHeight}
                                          fill="#1976d2"
                                          opacity="0.85"
                                      />
                                      {overheadHeight > 0 && (
                                          <rect
                                              x={xStart}
                                              y={overheadY}
                                              width={width}
                                              height={overheadHeight}
                                              fill="#90caf9"
                                              opacity="0.85"
                                          />
                                      )}
                                  </g>
                              );
                          })
                        : (() => {
                              const batchPoints = [];
                              const aiPoints = [];
                              relevantData.forEach((record) => {
                                  const mid =
                                      (Math.max(record.processing_start_ms, startTime) +
                                          Math.min(record.processing_end_ms, endTime)) /
                                      2;
                                  const x = ((mid - startTime) / rulerDuration) * CHART_WIDTH;
                                  const { batch, ai } = deriveMetrics(record);
                                  const batchY = CHART_HEIGHT - (batch / maxMs) * CHART_HEIGHT * 0.9;
                                  const aiY = CHART_HEIGHT - (ai / maxMs) * CHART_HEIGHT * 0.9;
                                  batchPoints.push(`${x},${batchY}`);
                                  aiPoints.push(`${x},${aiY}`);
                              });
                              return (
                                  <>
                                      <polyline
                                          points={batchPoints.join(' ')}
                                          fill="none"
                                          stroke="#90caf9"
                                          strokeWidth="2"
                                      />
                                      <polyline
                                          points={aiPoints.join(' ')}
                                          fill="none"
                                          stroke="#1976d2"
                                          strokeWidth="2"
                                      />
                                  </>
                              );
                          })()}
                </svg>
            </Box>

            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 12, height: 12, backgroundColor: '#1976d2', borderRadius: '2px' }} />
                    <Typography level="body-xs">AI processing (ms)</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 12, height: 12, backgroundColor: '#90caf9', borderRadius: '2px' }} />
                    <Typography level="body-xs">
                        {mode === 'stacked' ? 'Non-AI overhead (ms)' : 'Batch wall time (ms)'}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
}
